import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { SquaresFour, Archive, BookOpenText, Article, Books, SignOut, GearSix, ShieldStar } from "@phosphor-icons/react";
import MultiToolMark from "@/components/MultiToolMark";
import GuideLink from "@/components/GuideLink";

const navItems = [
  { to: "/dashboard", label: "Workspace", icon: SquaresFour, testid: "nav-dashboard" },
  { to: "/library", label: "Library", icon: Archive, testid: "nav-library" },
  { to: "/blog", label: "Insights", icon: Article, testid: "nav-blog" },
  { to: "/reading-list", label: "Reading List", icon: Books, testid: "nav-reading-list" },
  { to: "/guide", label: "How & Why", icon: BookOpenText, testid: "nav-guide" },
  { to: "/settings", label: "Settings", icon: GearSix, testid: "nav-settings" },
];

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const items = user?.is_admin
    ? [...navItems, { to: "/admin", label: "Admin", icon: ShieldStar, testid: "nav-admin" }]
    : navItems;

  return (
    <div className="min-h-screen bg-slate-50 font-['IBM_Plex_Sans',sans-serif] print:bg-white">
      <aside className="fixed inset-y-0 left-0 w-64 bg-slate-900 border-r border-slate-800 text-slate-300 flex flex-col print:hidden">
        <button
          data-testid="brand-home"
          onClick={() => navigate("/dashboard")}
          className="px-6 py-5 border-b border-slate-800 flex items-center gap-2.5 text-left hover:bg-slate-800/40 transition-colors"
        >
          <MultiToolMark size={40} className="shrink-0" />
          <div className="leading-none">
            <span className="font-mono text-lg font-bold tracking-tight text-white">MODUS</span>
            <p className="mt-1 text-[10px] tracking-[0.14em] uppercase text-slate-500">Your Leadership Multi-Tool</p>
          </div>
        </button>

        <nav className="flex-1 px-3 py-4 space-y-1">
          {items.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              data-testid={item.testid}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-sm text-sm transition-colors ${
                  isActive
                    ? "bg-slate-800 text-white font-medium border-l-4 border-indigo-500"
                    : "hover:bg-slate-800 hover:text-white border-l-4 border-transparent"
                }`
              }
            >
              <item.icon size={20} weight="duotone" />
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="border-t border-slate-800 p-4">
          <div className="flex items-center gap-3 mb-3">
            {user?.picture ? (
              <img src={user.picture} alt="" className="w-9 h-9 rounded-sm object-cover" />
            ) : (
              <div className="w-9 h-9 rounded-sm bg-slate-700 flex items-center justify-center text-white text-sm font-semibold">
                {user?.name?.[0] || "U"}
              </div>
            )}
            <div className="min-w-0">
              <p className="text-sm font-medium text-white truncate">{user?.name}</p>
              <p className="text-xs text-slate-400 truncate">{user?.email}</p>
            </div>
          </div>
          <button
            data-testid="logout-button"
            onClick={logout}
            className="w-full inline-flex items-center justify-center gap-2 rounded-sm border border-slate-700 px-3 py-2 text-sm text-slate-300 hover:bg-slate-800 hover:text-white transition-colors"
          >
            <SignOut size={18} /> Sign out
          </button>
        </div>
      </aside>

      <main className="lg:pl-64 print:pl-0"><GuideLink />{children}</main>
    </div>
  );
}
